<template>
  <b-card id="station-info" @scroll.prevent="">
    <b-card-text>
      <b-button @click="close" class="float-right"><span class="fa fa-close"></span></b-button>
      <h3>{{ value.name }}</h3><br>
      <div class="alert alert-warning" v-if="value.price === null">
        <strong>Important:</strong> This station hasn't been confirmed by anyone recently. If you visit or have visited recently, please let everyone know that <strong>{{ value.name }}</strong> is still there by updating the price of LPG. If LPG is no longer sold here, please <router-link :to="'/edit-station/' + value.id">mark this station for removal</router-link>.
      </div>
      <table>
        <tr><th>Address</th><td>{{ value.address }}, {{ value.postcode }}</td></tr>
        <tr v-if="value.connectors && value.connectors.length > 0"><th>Connectors</th><td>{{ toTitleCaseArray(value.connectors).join(', ') }}</td></tr>
        <tr><th>Phone</th><td>{{ value.phone }}</td></tr>
        <tr v-if="value.payment_methods && value.payment_methods.length > 0"><th>Payment</th><td>{{ toTitleCaseArray(value.payment_methods).join(', ') }}</td></tr>
        <tr><th>Price/litre</th>
          <td>
            <span v-if="value.price > 0">{{ value.price }} (reported on {{ formattedUpdateDate }})</span>
            <span class="text-danger" v-if="value.price == 0.0">Autogas Temporarily Unavailable (last update {{ formattedUpdateDate }})</span>
            <span class="text-info" v-if="value.price === null">Unknown</span>
          </td>
        </tr>
        <tr><th>Notes</th><td>{{ value.notes }}</td></tr>
      </table>
      <hr>

      <update-price v-bind:name="value.name" v-bind:priceUpdated="value.priceUpdated" v-bind:priceUpdating="value.priceUpdating" v-bind:passwordRequired="value.passwordRequired"  v-bind:stationId="value.id" v-bind:price="value.price" v-bind:stationName="value.name"/>

      <hr>

      <details v-if="value.opening_hours && Object.keys(value.opening_hours).length > 0">
        <summary>
          <span v-if="openNow" class="station-open">
            <span v-if="openAllNight">
              Open all night
            </span>
            <span v-else>
              Open until {{ endTime }}
            </span>
          </span><span v-if="!openNow" class="station-closed">Closed now</span>&nbsp;<strong>View full opening hours?</strong>
        </summary>
        <table>
          <tr>
            <td>
              <table class="opening-hours">
                <tr v-for="day in ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']" :class="{'current-day': new Date().toLocaleDateString('en-GB', { weekday: 'long' }) === days[day]}">
                  <td>{{ days[day] }}</td>
                  <td v-if="value.opening_hours[day]">
                    <span v-for="period in value.opening_hours[day]">
                      <span v-if="period.opens === '00:00' && period.closes === '23:59'">
                        Open 24 hours
                      </span>
                      <span v-else>{{ period.opens }} - {{ period.closes }}</span>
                    </span>
                  </td>
                  <td v-else>
                    Closed
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </details>
      <div v-else>
        Opening hours are not available for this station. Please help out by <router-link :to="'/edit-station/' + value.id">adding them</router-link>.
      </div>
      <hr>
      <b-button v-if="value.phone" :href="'tel:' + value.phone"><span class="fa fa-phone"></span> Phone</b-button><b-button v-if="value.website" :href="value.website" target="_blank"><span class="fa fa-external-link"></span> Website</b-button><b-button target="_blank" :href="'https://maps.google.com?f=d&dirflg=d&daddr='+value.location.y+','+value.location.x"><span class="fa fa-location-arrow"></span> Navigate</b-button>

        <hr>

        <h5>Comments</h5>

        <vue-cusdis
            :attrs="{
      host: 'https://autogasapp-cusdis.vercel.app',
      appId: '718f76ac-ec86-4b75-a642-75bcf0e3058e',
      pageId: value.identifier}" :key="value.identifier"></vue-cusdis>

    </b-card-text>
  </b-card>
</template>

<style scoped>
  #station-info {
    margin: 1mm;
    display: inline-block;
    vertical-align: top;
    position: absolute;
    right: 5mm;
    top: 22mm;
    width: 40%;
    max-width: 200mm;
    bottom: 5mm;
    z-index: 1000;
    overflow: auto;
  }

  #station-info .btn {
    margin: 1mm;
  }

  @media only screen and (max-width: 1120px) and  (min-width: 751px) {
    #station-info {
      display: block;
      vertical-align: top;
      position: fixed;
      right: 5mm;
      left: 5mm;
      bottom: 5mm;
      width: 90%;
      z-index: 1000;
      margin: 0 auto;
    }
  }

  @media only screen and (max-width: 750px) {
    #station-info {
      position: absolute;
      display: block;
      vertical-align: top;
      right: 0;
      left: 0;
      bottom: 0;
      top: 0;
      z-index: 1000;
      margin: 0;
      padding: 0;
      width: 100%;
    }
  }

  table{
    width: 100%;
  }

  .opening-hours td:first-child{
    text-align: left;
  }
  .opening-hours td:nth-child(2){
    text-align: right;
  }

  .opening-hours {
    padding-left: 10mm;
    padding-right: 10mm;
  }
</style>

<script>
  import { days } from '../variables.js'
  import { toTitleCaseArray } from '../utils.js'
  import UpdatePrice from "./UpdatePrice";
  import VueCusdis from 'vue-cusdis/dist/vue2.es';

  export default {
    components: {UpdatePrice, VueCusdis},
    data () {
      return {
        'days': days,
        'toTitleCaseArray': toTitleCaseArray,
        'openNow': false,
        'endTime': null,
        'openAllNight': null,
        'showUpdatePrice': false,
      }
    },
    methods: {
      close () {
        this.$emit('close');
      }
    },
    name: 'StationInfo',
    props: {
      value: Object,
    },
    watch: {
      value: {
        immediate: true,
        handler (val) {
          let date = new Date(val.price_last_updated_at);
          this.formattedUpdateDate = date.toLocaleDateString();
          console.log(this.formattedUpdateDate)
          let dateToday = new Date();
          let dateTomorrow = new Date();
          dateTomorrow.setDate(new Date().getDate()+1);
          let today = val.opening_hours[dateToday.toLocaleDateString('en-GB', { weekday: 'short' }).toLowerCase()];
          let tomorrow = val.opening_hours[dateTomorrow.toLocaleDateString('en-GB', { weekday: 'short' }).toLowerCase()];
          this.openNow = false;
          this.endTime = null;
          this.openAllNight = false;

          if(today){
            for(let i = 0; i < today.length; i++){
              let currentDate = new Date();
              let startPeriod = new Date();
              startPeriod.setHours(today[i].opens.split(':')[0], today[i].opens.split(':')[1], 0);
              let endPeriod = new Date();
              endPeriod.setHours(today[i].closes.split(':')[0], today[i].closes.split(':')[1], 0);
              if(currentDate >= startPeriod && currentDate < endPeriod){
                this.openNow = true;
                this.endTime = today[i].closes;

                if(today[i].closes === '23:59'){
                  if(tomorrow && tomorrow[0].opens === '00:00'){
                    this.openAllNight = true;
                  }
                }
              }
            }
          }
        }
      }
    }
  }
</script>